import axiosInstance from "../../api/axiosInstance";

/**
 * Backend README'ye göre güncellenmiş Dashboard API
 * Ana Endpoint: GET /api/dashboard
 */

export interface DashboardOverview {
  total_users: number;
  total_songs: number;      // Backend'de total_songs (v1)
  total_songsv2: number;    // Backend'de total_songsv2
  total_beats: number;
  total_song_types: number;
  total_song_requests: number;
}

export interface RecentActivities {
  recent_users: any[];
  recent_songs: any[];      // Backend'de recent_songs (v1)
  recent_songsv2: any[];   // Backend'de recent_songsv2
  recent_requests: any[];
}

export interface SongsStatistics {
  songs_v1: {
    by_songtype: Record<string, number>;
    by_rhythm: Record<string, number>;
    avg_bpm: number;
    avg_duration_seconds: number;
  };
  songs_v2: {
    by_status: Record<string, number>;
    by_difficulty: Record<string, number>;
    total_plays: number;
    total_likes: number;
    avg_bpm: number;
  };
}

export interface PopularContent {
  popular_songsv2: any[];         // Backend'de popular_songsv2
  most_liked_songsv2: any[];      // Backend'de most_liked_songsv2
  popular_song_types: any[];
}

export interface SongRequestsSummary {
  by_status: {
    pending: number;
    approved: number;
    rejected: number;
  };
  total: number;
  recent_pending: any[];
}

export interface GrowthMetrics {
  period_days: number;
  new_users: number;
  new_songs_v1: number;
  new_songs_v2: number;
  new_requests: number;
  cutoff_date: string;
}

export interface DashboardData {
  overview: DashboardOverview;
  recent_activities: RecentActivities;
  songs_statistics: SongsStatistics;
  popular_content: PopularContent;
  song_requests_summary: SongRequestsSummary;
  growth_metrics: GrowthMetrics;
  generated_at: string;
}

export interface DashboardApiResponse {
  success: boolean;
  message: string;
  data: DashboardData;
}

/**
 * MAIN ENDPOINT - Backend README'ye göre tek endpoint kullanımı
 * GET /api/dashboard - Complete dashboard data in a single request
 * 
 * @param params.recent_limit - Limit for recent items (default: 10)
 * @param params.popular_limit - Limit for popular content (default: 10)
 * @param params.growth_days - Days for growth metrics (default: 30)
 * @param params.include - Get specific sections only (comma-separated)
 *                        Values: overview, recent, songs_stats, popular, requests, growth
 */
export const fetchDashboard = async (params?: {
  recent_limit?: number;
  popular_limit?: number;
  growth_days?: number;
  include?: string; // e.g., "overview,growth"
}): Promise<DashboardData> => {
  const response = await axiosInstance.get<DashboardApiResponse>("/dashboard", { params });
  return response.data.data;
};

/**
 * Convenience function: Get only overview and growth (fast query)
 */
export const fetchDashboardQuickStats = async (): Promise<Pick<DashboardData, 'overview' | 'growth_metrics'>> => {
  const response = await axiosInstance.get<DashboardApiResponse>("/dashboard", {
    params: { include: "overview,growth" }
  });
  return response.data.data as Pick<DashboardData, 'overview' | 'growth_metrics'>;
};

/**
 * Legacy support - Deprecated, use fetchDashboard instead
 * @deprecated Use fetchDashboard() with params instead
 */
export const fetchDashboardSummary = async (params?: {
  recent_limit?: number;
  popular_limit?: number;
  growth_days?: number;
}): Promise<DashboardData> => {
  console.warn('fetchDashboardSummary is deprecated, use fetchDashboard instead');
  return fetchDashboard(params);
};
